<template>
  <div class="lucky-referral">
    <!-- Header -->
    <header class="lr-header">
      <button class="lr-header__back" @click="$router.go(-1)">
        <van-icon name="arrow-left" size="20" color="#ffffff" />
      </button>
      <h1 class="lr-header__title">{{ $lang.lr_title || 'LUCKY REFERRAL' }}</h1>
      <div class="lr-header__spacer"></div>
    </header>

    <!-- Dashboard Card -->
    <section class="lr-dashboard">
      <!-- Target Reward -->
      <div class="lr-target">
        <p class="lr-target__label">{{ $lang.lr_target_reward }}</p>
        <div class="lr-target__plaque">
          <h2 class="lr-target__amount">{{ getCurrency }} {{ $formatNumberWithCommas(targetReward) }}</h2>
        </div>
      </div>

      <!-- Current Earnings -->
      <div class="lr-earnings">
        <div class="lr-earnings__info">
          <p class="lr-earnings__label">{{ $lang.lr_current_earnings }}</p>
          <span class="lr-earnings__amount">{{ getCurrency }} {{ $formatNumberWithCommas(currentEarnings) }}</span>
        </div>
        <img
          class="lr-earnings__token"
          src="../../../assets/img/otgame/amount.png"
          width="40"
        />
      </div>

      <!-- Progress Bar -->
      <div class="lr-progress">
        <div class="lr-progress__track">
          <div
            class="lr-progress__fill"
            :class="{ 'lr-progress__fill--empty': progressPercent === 0 }"
            :style="{ width: progressPercent + '%' }"
          >
            <div v-if="progressPercent > 0" class="lr-progress__glow"></div>
            <span v-if="progressPercent > 0" class="lr-progress__text">{{ progressPercent }}%</span>
          </div>
          <span v-if="progressPercent === 0" class="lr-progress__text lr-progress__text--center">0%</span>
        </div>
        <p class="lr-progress__hint">
          {{ $lang.lr_spins_hint }}
        </p>
      </div>

      <!-- Claim Button -->
      <button
        class="lr-claim"
        :class="{ 'lr-claim--active': canClaim }"
        :disabled="!canClaim"
        @click="claimReward"
      >
        <van-icon v-if="!canClaim" name="lock" size="18" />
        <span>{{ $lang.lr_claim_reward }}</span>
      </button>
    </section>

    <section v-if="readFailureMessage" class="lr-diagnostic">
      {{ readFailureMessage }}
    </section>

    <!-- Wheel Visual -->
    <section class="lr-wheel-section">
      <div class="lr-wheel-container">
        <div class="lr-wheel" :style="{ transform: `rotate(${wheelAngle}deg)` }">
          <img
            src="../../../assets/img/activity/activity_banner/LuckyWheel.png"
            class="lr-wheel__img"
          />
        </div>
        <!-- Inner overlay -->
        <div class="lr-wheel__border"></div>
        <!-- Pointer -->
        <div class="lr-wheel__pointer"></div>
        <!-- Center dot -->
        <div class="lr-wheel__center">
          <div class="lr-wheel__center-dot"></div>
        </div>
      </div>

      <button
        class="lr-spin-btn"
        :disabled="isSpinning"
        @click="spin"
      >
        {{ isSpinning ? $lang.lr_spinning : ($lang.lr_spin_x || 'SPIN') + ' x' + remainingSpins }}
      </button>
    </section>

    <!-- Referral Link -->
    <section class="lr-referral">
      <label class="lr-referral__label">{{ $lang.lr_referral_link }}</label>
      <div class="lr-referral__row">
        <div class="lr-referral__link">{{ referralLink }}</div>
        <button class="lr-referral__copy" @click="copyLink">{{ $lang.lr_copy }}</button>
      </div>
    </section>

    <!-- Rules -->
    <section class="lr-rules">
      <p class="lr-rules__text">
        <span class="lr-rules__highlight">{{ $lang.lr_rules_highlight }}</span>
        {{ $lang.lr_rules_desc }}
      </p>
    </section>

    <!-- Recent Winners -->
    <section class="lr-winners">
      <div class="lr-winners__header">
        <h3 class="lr-winners__title">{{ $lang.lr_recent_winners }}</h3>
        <span class="lr-winners__live">{{ $lang.lr_live_feed || 'Live Feed' }}</span>
      </div>
      <div class="lr-winners__table">
        <div class="lr-winners__thead">
          <span>{{ $lang.lr_player_name }}</span>
          <span>{{ $lang.lr_reward_amount }}</span>
        </div>
        <div class="lr-winners__marquee">
          <div class="lr-winners__marquee-content" ref="marqueeContent">
            <div
              v-for="(winner, idx) in winnersDouble"
              :key="idx"
              class="lr-winners__row"
            >
              <span class="lr-winners__name">{{ winner.name }}</span>
              <span class="lr-winners__amount">{{ getCurrency }} {{ $formatNumberWithCommas(winner.amount) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Insufficient Spins Popup -->
    <van-popup
      v-model="showNoSpins"
      round
      :close-on-click-overlay="true"
      class="lr-popup-wrapper"
    >
      <div class="lr-popup">
        <div class="lr-popup__icon">
          <van-icon name="warning-o" size="40" color="#ff5a1f" />
        </div>
        <h2 class="lr-popup__title">{{ $lang.lr_insufficient_spins }}</h2>
        <p class="lr-popup__desc">{{ $lang.lr_invite_more }}</p>

        <!-- Social Share Icons -->
        <div class="lr-popup__socials">
          <a class="lr-popup__social lr-popup__social--fb" @click="shareToSocial('x')">
            <img src="../../../assets/img/otgame/pinduoduo_24.png" width="24" />
          </a>
          <a class="lr-popup__social lr-popup__social--tg" @click="shareToSocial('facebook')">
            <img src="../../../assets/img/otgame/zjm_39.png" width="24" />
          </a>
          <a class="lr-popup__social lr-popup__social--wa" @click="shareToSocial('whatsapp')">
            <img src="../../../assets/img/otgame/zjm_42.png" width="24" />
          </a>
          <a class="lr-popup__social lr-popup__social--x" @click="shareToSocial('ins')">
            <img src="../../../assets/img/otgame/ins.png" width="24" />
          </a>
        </div>

        <!-- Referral Link in popup -->
        <div class="lr-popup__ref">
          <p class="lr-popup__ref-label">{{ $lang.lr_referral_link }}</p>
          <div class="lr-popup__ref-row">
            <div class="lr-popup__ref-link">{{ referralLink }}</div>
            <button class="lr-popup__ref-copy" @click="copyLink">{{ $lang.lr_copy }}</button>
          </div>
        </div>

        <!-- Close Button -->
        <button class="lr-popup__close" @click="showNoSpins = false">
          {{ $lang.lr_close }}
        </button>
      </div>
    </van-popup>
    <login v-model="showLogin" overlay></login>
  </div>
</template>

<script>
import {
  GetLuckyRoulette,
  LuckyRoulette,
  ReceiveLuckyRouletteBonus,
  GetLuckyRouletteLatestAwards
} from '@/api/common'
import { reportPromoPanel } from '@/utils/common'
import {
  failMessage as rewardFailMessage,
  hasRewardIssueProof,
  hasUnavailableMarker,
  isClaimSuccess as rewardClaimSuccess,
  isNeedLogin,
  isOkResponse,
  isRouletteSuccess as rewardRouletteSuccess,
  responseContent
} from './rewardApi'
import { safeGetStorageItem, safeLocalStorage } from '@/utils/safeStorage'
import { openExternalUrl } from '@/views/layout/safeLayout'
import { copyTextToClipboard } from '@/components/share/shareSafety'

function getLocalStorage() {
  try {
    if (typeof safeLocalStorage === 'function') return safeLocalStorage()
  } catch (e) {
    return null
  }
  return null
}

function getLocationHost() {
  try {
    if (typeof window !== 'undefined') {
      const windowLocation = window.location
      const value = windowLocation && windowLocation['host']
      return value ? String(value) : ''
    }
  } catch (e) {
    return ''
  }
  return ''
}

function isPromiseLike(value) {
  return value && typeof value.then === 'function'
}

export default {
  name: 'LuckyReferral',
  data() {
    return {
      // API data
      targetReward: 0,
      currentEarnings: 0,
      remainingSpins: 0,
      invitedCount: 0,
      isOpen: 0,
      receiveStatus: 0, // 0=抽奖中 1=等待领取 2=已领取 3=已过期
      freeTimes: 0,
      minInviteCountToReward: 0,
      rewardWritable: true,
      readFailureMessage: '',
      // UI state
      isSpinning: false,
      wheelAngle: 0,
      showNoSpins: false,
      winners: [],
      showLogin: false
    }
  },
  computed: {
    progressPercent() {
      if (!this.targetReward) return 0
      return Math.min(
        Math.round((this.currentEarnings / this.targetReward) * 1000) / 10,
        100
      )
    },
    canClaim() {
      return this.rewardWritable && this.receiveStatus === 1
    },
    spinsToTarget() {
      if (!this.targetReward) return 0
      const remaining = this.targetReward - this.currentEarnings
      return remaining > 0 ? Math.ceil(remaining / 100) : 0
    },
    referralLink() {
      const storage = getLocalStorage()
      const host = getLocationHost()
      const code = safeGetStorageItem(storage, 'shareCode')
      if (!host) return ''
      if (!code) return ''
      return `${host}/${this.GET_COUNTRY}/home?id=${code}`
    },
    winnersDouble() {
      return [...this.winners, ...this.winners]
    }
  },
  mounted() {
    reportPromoPanel('lucky_roulette', 1)
    if (!this.token) {
      this.requireLogin()
      return
    }
    this.fetchData()
    this.fetchWinners()
  },
  beforeDestroy() {
    reportPromoPanel('lucky_roulette', 2)
  },
  methods: {
    requireLogin() {
      this.showLogin = true
      if (this.$bus) this.$bus.$emit('openLogin')
    },
    failMessage(res, fallback) {
      return rewardFailMessage(res, fallback || this.$lang.mc_claim_failed || 'This feature is temporarily unavailable. Please try again later.')
    },
    isRewardReadUnavailable(res) {
      const root = res && res.response && res.response.data ? res.response.data : res
      const stack = [root, responseContent(root)]
      const seen = new Set()
      while (stack.length) {
        const source = stack.shift()
        if (!source || typeof source !== 'object' || seen.has(source)) continue
        seen.add(source)
        if (hasUnavailableMarker(source)) return true
        const marker = [
          source.fallbackSource,
          source.fallback_source,
          source.source,
          source.reason,
          source.blockedReason,
          source.blocked_reason
        ].filter(Boolean).join(' ').toLowerCase()
        if (marker.includes('read-unavailable') ||
          marker.includes('readunavailable') ||
          marker.includes('missingtable') ||
          marker.includes('missing_table') ||
          marker.includes('missing table') ||
          marker.includes('missingcolumn') ||
          marker.includes('missing_column') ||
          marker.includes('missing column')) return true
        ;['content', 'data', 'body', 'detail', 'diagnostics', 'diagnostic', 'payload', 'extra'].forEach(key => {
          const value = source[key]
          if (value && typeof value === 'object') stack.push(value)
        })
      }
      return false
    },
    setReadFailure(res, fallback) {
      if (this.isRewardReadUnavailable(res)) {
        this.readFailureMessage = ''
        return
      }
      this.readFailureMessage = this.failMessage(res, fallback || this.$lang.reward_no_data || 'No rewards found')
      this.$toast({ message: this.readFailureMessage, icon: 'cross' })
    },
    firstValue(...values) {
      return values.find(value => value !== undefined && value !== null && value !== '')
    },
    safeNumber(value, fallback = 0) {
      const n = Number(value)
      return Number.isFinite(n) ? n : fallback
    },
    firstObject(...values) {
      for (const value of values) {
        if (Array.isArray(value)) {
          const row = value.find(item => item && typeof item === 'object')
          if (row) return row
        } else if (value && typeof value === 'object') {
          return value
        }
      }
      return {}
    },
    firstArray(...values) {
      for (const value of values) {
        if (Array.isArray(value)) return value
      }
      return []
    },
    normalizeRouletteContent(content) {
      const source = content && typeof content === 'object' ? content : {}
      return {
        ...source,
        ...this.firstObject(source.records, source.record, source.list, source.dataList, source.items, source.pageList)
      }
    },
    isFalseFlag(value) {
      return value === false || value === 0 || ['0', 'false', 'no', 'off'].includes(String(value == null ? '' : value).trim().toLowerCase())
    },
    isRewardWritableSource(source) {
      if (!source || typeof source !== 'object') return false
      return !this.hasWriteGuardMarker(source) &&
        !this.isFalseFlag(source.rewardWritable) &&
        !this.isFalseFlag(source.drawWritable) &&
        !this.isFalseFlag(source.claimWritable) &&
        !this.isFalseFlag(source.writable) &&
        !this.isFalseFlag(source.writeEnabled)
    },
    hasWriteGuardMarker(source, depth = 0) {
      if (!source || typeof source !== 'object' || depth > 5) return false
      if (hasUnavailableMarker(source)) return true
      const blockingKeys = ['readOnly', 'readOnlyMode', 'readonly', 'read_only', 'read_only_mode', 'mock', 'mockMode', 'mock_mode', 'mockFallback', 'mock_fallback', 'stub', 'unimplemented', 'notImplemented', 'not_implemented', 'notSupported', 'not_supported', 'unsupported', 'failed', 'failClosed', 'fail_closed', 'writeBlocked', 'write_blocked', 'fallback', 'fallbackUsed', 'fallback_used', 'dataUnavailable', 'data_unavailable', 'readFailure', 'read_failure', 'readUnavailable', 'read_unavailable', 'routeUnavailable', 'route_unavailable', 'technicalError', 'technical_error', 'technicalMarker', 'technical_marker', 'technicalException', 'technical_exception', 'backendFailure', 'backend_failure', 'notConnected', 'not_connected', 'falseProof', 'false_proof', 'paymentStatus', 'payment_status', 'paymentStatusUnconfirmed', 'payment_status_unconfirmed', 'unconfirmed']
      const blockingTokens = ['mock', 'mock_mode', 'mock mode', 'mock-mode', 'mock_fallback', 'read_only', 'read_only_mode', 'read only mode', 'read-only-mode', 'readonly', 'readonlymode', 'read only', 'read-only', 'stub', 'unimplemented', 'not_implemented', 'not_supported', 'not supported', 'unsupported', 'failed', 'failclosed', 'fail_closed', 'fail closed', 'fail-closed', 'write_blocked', 'writeblocked', 'write blocked', 'write-blocked', 'fallback', 'fallback_used', 'fallback used', 'fallback-used', 'data_unavailable', 'data unavailable', 'data-unavailable', 'dataunavailable', 'readfailure', 'read_failure', 'read failure', 'read-failure', 'readunavailable', 'read_unavailable', 'read unavailable', 'read-unavailable', 'routeunavailable', 'route_unavailable', 'route unavailable', 'route-unavailable', 'technical_error', 'technical error', 'technical-error', 'technicalmarker', 'technical_marker', 'technical marker', 'technical-marker', 'technical_exception', 'technical exception', 'technical-exception', 'backendfailure', 'backend_failure', 'backend failure', 'backend-failure', 'falseproof', 'false_proof', 'false proof', 'false-proof', 'paymentstatusunconfirmed', 'payment_status_unconfirmed', 'payment status unconfirmed', 'payment-status-unconfirmed', 'unconfirmed', '技术错误', '技术异常']
      const isActive = value => value === true || value === 1 || blockingTokens.some(token => String(value == null ? '' : value).trim().toLowerCase().includes(token))
      if (blockingKeys.some(key => isActive(source[key]))) return true
      if ([source.status, source.state, source.result, source.code, source.errorCode, source.msg, source.message, source.reason, source.desc, source.description].some(isActive)) return true
      return ['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'error', 'errors', 'detail', 'details'].some(key => this.hasWriteGuardMarker(source[key], depth + 1))
    },
    isClaimSuccess(res) {
      return !this.hasWriteGuardMarker(res) && rewardClaimSuccess(res) && hasRewardIssueProof(res)
    },
    isRouletteSuccess(res) {
      if (this.hasWriteGuardMarker(res)) return false
      return rewardRouletteSuccess(res) && hasRewardIssueProof(res)
    },
    resolvePrizeIndex(content) {
      const rawIndex = content && this.firstValue(
        content.prizeIndex,
        content.targetIndex,
        content.awardIndex,
        content.index,
        content.prizeNo,
        content.position
      )
      const prizeCount = this.safeNumber(content && content.prizeCount, 8)
      const index = Number(rawIndex)
      if (Number.isFinite(index) && prizeCount > 0) {
        return Math.abs(Math.floor(index)) % prizeCount
      }
      return 0
    },
    applyRouletteState(content) {
      const d = this.normalizeRouletteContent(content)
      const rules = this.firstObject(d.rules, d.rule, d.config)
      const writable = this.isRewardWritableSource(d)
      this.rewardWritable = writable
      this.currentEarnings = this.safeNumber(this.firstValue(d.bonusSum, d.currentEarnings, d.earnings, d.amount), this.currentEarnings)
      this.remainingSpins = writable ? this.safeNumber(this.firstValue(d.drawCount, d.remainingSpins, d.freeTimesLeft, d.chance), this.remainingSpins) : 0
      this.invitedCount = this.safeNumber(this.firstValue(d.invitedCount, d.inviteCount), this.invitedCount)
      this.isOpen = this.safeNumber(d.isOpen, this.isOpen)
      this.receiveStatus = this.safeNumber(this.firstValue(d.receiveStatus, d.status), this.receiveStatus)
      this.targetReward = this.safeNumber(this.firstValue(rules.bonusAmount, d.targetReward, d.targetAmount), this.targetReward)
      this.freeTimes = this.safeNumber(this.firstValue(rules.freeTimes, d.freeTimes), this.freeTimes)
      this.minInviteCountToReward = this.safeNumber(
        this.firstValue(rules.minInviteCountToReward, d.minInviteCountToReward),
        this.minInviteCountToReward
      )
    },
    normalizeWinners(content) {
      const source = content && typeof content === 'object' ? content : {}
      const list = this.firstArray(
        source.list,
        source.awardList,
        source.records,
        source.latestAwards,
        source.dataList,
        source.items,
        source.pageList
      )
      if (!Array.isArray(list)) return []
      return list.map(item => ({
        name: this.firstValue(item.userAccount, item.account, item.name, item.userName, item.username, item.playerName, ''),
        amount: this.safeNumber(this.firstValue(item.amount, item.bonusSum, item.rewardAmount, item.reward_amount, item.reward, item.bonusAmount))
      })).filter(item => item.name || item.amount > 0)
    },
    async fetchData() {
      try {
        const res = await GetLuckyRoulette({})
        if (isNeedLogin(res)) {
          this.requireLogin()
        } else if (hasUnavailableMarker(res) || this.hasWriteGuardMarker(res)) {
          this.setReadFailure(res)
        } else if (isOkResponse(res)) {
          this.readFailureMessage = ''
          this.applyRouletteState(responseContent(res))
        } else {
          this.setReadFailure(res)
        }
      } catch (e) {
        this.setReadFailure(e, '活动请求失败，请稍后重试。')
      }
    },
    async fetchWinners() {
      try {
        const res = await GetLuckyRouletteLatestAwards({})
        if (isNeedLogin(res)) {
          this.requireLogin()
        } else if (hasUnavailableMarker(res)) {
          if (!this.isRewardReadUnavailable(res)) this.$toast({ message: this.failMessage(res, this.$lang.reward_no_data || 'No rewards found'), icon: 'cross' })
        } else if (isOkResponse(res)) {
          this.winners = this.normalizeWinners(responseContent(res))
        } else {
          if (!this.isRewardReadUnavailable(res)) this.$toast({ message: this.failMessage(res, this.$lang.reward_no_data || 'No rewards found'), icon: 'cross' })
        }
      } catch (e) {
        if (this.isRewardReadUnavailable(e)) return
        this.$toast({ message: this.failMessage(e, '活动请求失败，请稍后重试。'), icon: 'cross' })
      }
    },
    async spin() {
      if (this.isSpinning) return
      if (!this.token) {
        this.requireLogin()
        return
      }
      if (!this.rewardWritable) {
        this.$toast({ message: this.failMessage(null, 'This feature is temporarily unavailable. Please try again later.'), icon: 'cross' })
        return
      }
      if (this.remainingSpins <= 0) {
        this.showNoSpins = true
        return
      }
      this.isSpinning = true

      try {
        const res = await LuckyRoulette({})
        if (isNeedLogin(res)) {
          this.isSpinning = false
          this.requireLogin()
          return
        }
        if (!this.isRouletteSuccess(res)) {
          this.isSpinning = false
          this.$toast({ message: this.failMessage(res, 'This feature is temporarily unavailable. Please try again later.'), icon: 'cross' })
          return
        }
        const d = responseContent(res)
        const prevEarnings = this.currentEarnings
        const targetIndex = this.resolvePrizeIndex(d)
        const prizeCount = Math.max(this.safeNumber(d.prizeCount, 8), 1)
        const sectorAngle = 360 / prizeCount
        const extraDeg = 360 * 5 + targetIndex * sectorAngle + sectorAngle / 2
        this.wheelAngle += extraDeg
        setTimeout(() => {
          this.isSpinning = false
          this.applyRouletteState(d)
          const earned = this.currentEarnings - prevEarnings
          if (earned > 0) {
            this.$toast({
              message: `+${this.getCurrency} ${this.$formatNumberWithCommas(earned)}`,
              icon: 'success'
            })
          }
        }, 3000)
      } catch (e) {
        this.isSpinning = false
        this.$toast({ message: this.failMessage(e, '活动请求失败，请稍后重试。'), icon: 'cross' })
      }
    },
    async claimReward() {
      if (!this.rewardWritable) {
        this.$toast({ message: this.failMessage(null, 'This feature is temporarily unavailable. Please try again later.'), icon: 'cross' })
        return
      }
      if (!this.canClaim) return
      if (!this.token) {
        this.requireLogin()
        return
      }
      try {
        const res = await ReceiveLuckyRouletteBonus({})
        if (isNeedLogin(res)) {
          this.requireLogin()
        } else if (this.isClaimSuccess(res)) {
          const d = responseContent(res)
          this.applyRouletteState(d)
          const amount = this.firstValue(d.amount, d.rewardAmount, d.reward, d.bonus, d.bonusAmount)
          this.$toast({
            message: amount ? `+${this.getCurrency} ${this.$formatNumberWithCommas(amount)}` : (this.$lang.Sucesso || 'Success'),
            icon: 'success'
          })
        } else {
          this.$toast({ message: this.failMessage(res, 'This feature is temporarily unavailable. Please try again later.'), icon: 'cross' })
        }
      } catch (e) {
        this.$toast({ message: this.failMessage(e, '活动请求失败，请稍后重试。'), icon: 'cross' })
      }
    },
    copyLink() {
      if (!this.token) {
        this.requireLogin()
        return
      }
      const text = this.referralLink
      if (!text) {
        this.$toast({ message: this.$lang.noempt || 'No data', icon: 'cross' })
        return
      }
      let copied = false
      try {
        copied = copyTextToClipboard(text)
      } catch (e) {
        copied = false
      }
      this.$toast({
        message: copied ? (this.$lang.Sucesso || 'Success') : (this.$lang.copy_failed || 'Copy failed'),
        icon: copied ? 'success' : 'cross'
      })
    },
    shareToSocial(platform) {
      if (!this.token) {
        this.requireLogin()
        return
      }
      if (!this.referralLink) {
        this.$toast({ message: this.$lang.noempt || 'No data', icon: 'cross' })
        return
      }
      const encodedUrl = encodeURIComponent(this.referralLink)
      const encodedText = encodeURIComponent('VUKAVI')
      const links = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        ins: `https://www.instagram.com/?url=${encodedUrl}`,
        x: `https://x.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
        whatsapp: `https://api.whatsapp.com/send?text=${encodedUrl}`,
      }
      if (links[platform]) {
        const showShareFailure = () => {
          this.$toast({ message: this.$lang.share_not_available || 'Share channel is not available', icon: 'cross' })
        }
        try {
          const openResult = openExternalUrl(links[platform])
          if (isPromiseLike(openResult)) {
            openResult.then(success => {
              if (success === false) showShareFailure()
            }).catch(showShareFailure)
            return
          }
          if (openResult === false) showShareFailure()
        } catch (e) {
          showShareFailure()
        }
      } else {
        this.$toast({ message: this.$lang.share_not_available || 'Share channel is not available', icon: 'cross' })
      }
    }
  }
}
</script>

<style lang="less" scoped>
@primary: @brand;     /* 绿→火焰橙(强调/激活/CTA) */
@gold: #ffb020;       /* 黄→金(金额/奖) */
@bg-dark: @vk-bg;     /* 页面底 */
@card-bg: @vk-surface;/* 卡片面 */
@red: #f5455c;        /* 红保留语义(--vk-danger) */

.lucky-referral {
  min-height: 100vh;
  background: @bg-dark;
  padding-bottom: 30px;
}

// ====== HEADER ======
.lr-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  position: sticky;
  top: 0;
  z-index: 100;
  background: @bg-dark;

  &__back {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background: none;
    border: none;
    cursor: pointer;
  }

  &__title {
    font-size: var(--vk-fs-lg);
    font-weight: 700;
    letter-spacing: 2px;
    color: #ffffff;
  }

  &__spacer {
    width: 40px;
  }
}

// ====== DASHBOARD ======
.lr-dashboard {
  background: @card-bg;
  padding: 20px;
  border-radius: 10px;
  margin: 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6), 0 0 20px rgba(255, 90, 31, 0.03);
}

.lr-diagnostic {
  margin: 12px 16px 0;
  padding: 10px 12px;
  border: 1px solid rgba(248, 245, 16, 0.28);
  border-radius: 8px;
  background: rgba(248, 245, 16, 0.08);
  color: #fef08a;
  font-size: var(--vk-fs-xs);
  line-height: 1.4;
}

// Target Reward
.lr-target {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;

  &__label {
    font-size: var(--vk-fs-2xs);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 3px;
    color: #9ca3af;
    margin-bottom: 8px;
  }

  &__plaque {
    background: linear-gradient(145deg, #fffb80 0%, @gold 30%, #ca8a04 60%, #854d0e 100%);
    border: 1px solid #fef08a;
    box-shadow:
      inset 0 1px 2px rgba(255, 255, 255, 0.9),
      inset 0 -4px 15px rgba(0, 0, 0, 0.6),
      0 15px 35px rgba(0, 0, 0, 0.8);
    border-radius: 8px;
    padding: 12px 32px;
    width: 100%;
    max-width: 280px;
    text-align: center;
    position: relative;
    overflow: hidden;
    animation: plaque-glow 4s infinite ease-in-out;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(110deg, transparent 40%, rgba(255, 255, 255, 0.6) 50%, transparent 60%);
      animation: shine-plaque 4s infinite linear;
    }
  }

  &__amount {
    font-size: 48px;
    font-weight: 900;
    color: #000000;
    letter-spacing: -2px;
    text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.4);
    position: relative;
    z-index: 1;
  }
}

@keyframes plaque-glow {
  0% { box-shadow: 0 0 15px rgba(248, 245, 16, 0.3), 0 15px 30px rgba(0, 0, 0, 0.7); }
  50% { box-shadow: 0 0 35px rgba(248, 245, 16, 0.6), 0 15px 30px rgba(0, 0, 0, 0.7); }
  100% { box-shadow: 0 0 15px rgba(248, 245, 16, 0.3), 0 15px 30px rgba(0, 0, 0, 0.7); }
}

@keyframes shine-plaque {
  0% { transform: translateX(-150%); }
  100% { transform: translateX(150%); }
}

// Earnings
.lr-earnings {
  background: #0d121f;
  box-shadow:
    inset 0 4px 12px rgba(0, 0, 0, 0.9),
    inset 0 1px 2px rgba(255, 255, 255, 0.05),
    0 1px 0px rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.4);
  padding: 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  &__info {
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: var(--vk-t3);
  }

  &__amount {
    font-size: var(--vk-fs-2xl);
    font-weight: 900;
    color: #ffffff;
    letter-spacing: -0.5px;
  }

  &__token {
    filter: drop-shadow(0 10px 15px rgba(0, 0, 0, 0.8)) brightness(1.1);
  }
}

// Progress
.lr-progress {
  margin-bottom: 16px;

  &__track {
    background: #05070c;
    border: 1.5px solid rgba(255, 255, 255, 0.15);
    border-radius: 50px;
    box-shadow:
      inset 0 8px 12px rgba(0, 0, 0, 0.95),
      0 4px 15px rgba(0, 0, 0, 0.8);
    height: 34px;
    position: relative;
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    background:
      repeating-linear-gradient(45deg, rgba(204, 47, 25, 0.1) 0px, rgba(204, 47, 25, 0.1) 10px, transparent 10px, transparent 20px),
      linear-gradient(90deg, #8b1e10 0%, @red 50%, #8b1e10 100%);
    background-size: 40px 40px, 200% 100%;
    animation: energy-swirl 1.5s linear infinite;
    border-radius: 50px 0 0 50px;
    position: relative;
    box-shadow: inset 0 2px 8px rgba(255, 255, 255, 0.4), 0 0 25px rgba(220, 38, 38, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 50px;

    &--empty {
      background: transparent;
      animation: none;
      box-shadow: none;
      min-width: 0;
    }
  }

  &__glow {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 10px;
    background: white;
    filter: blur(3px);
    box-shadow:
      0 0 25px 6px @red,
      0 0 45px 12px rgba(255, 255, 255, 0.8);
    animation: pulsing-discharge 0.8s ease-in-out infinite alternate;
    z-index: 4;
  }

  &__text {
    font-size: var(--vk-fs-xs);
    font-weight: 900;
    color: #ffffff;
    z-index: 10;
    position: relative;

    &--center {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: rgba(255, 255, 255, 0.6);
    }
  }

  &__hint {
    text-align: center;
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    color: @primary;
    margin-top: 10px;
    animation: pulse-text 2s ease-in-out infinite;
    text-shadow: 0 0 8px rgba(255, 90, 31, 0.4);
  }
}

@keyframes energy-swirl {
  0% { background-position: 0 0, 100% 0; }
  100% { background-position: 40px 0, 0% 0; }
}

@keyframes pulsing-discharge {
  0% { opacity: 0.6; }
  100% { opacity: 1; }
}

@keyframes pulse-text {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

// Claim Button
.lr-claim {
  width: 100%;
  max-width: 220px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  border-radius: 10px;
  font-size: var(--vk-fs-xs);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  border: 1px solid rgba(255, 90, 31, 0.2);
  background: rgba(15, 23, 42, 0.5);
  color: #ffffff;
  cursor: not-allowed;

  &--active {
    background: @primary;
    color: #000000;
    border: none;
    cursor: pointer;
    box-shadow: 0 0 20px rgba(255, 90, 31, 0.6);

    &:active {
      transform: scale(0.95);
    }
  }
}

// ====== WHEEL ======
.lr-wheel-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px 8px;
}

.lr-wheel-container {
  position: relative;
  width: 280px;
  height: 280px;
  perspective: 1500px;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 120%;
    background: radial-gradient(circle, rgba(255, 90, 31, 0.15) 0%, transparent 70%);
    z-index: -1;
    pointer-events: none;
  }
}

.lr-wheel {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  background: #1a1a2e;
  border: 6px solid #2f323d;
  box-shadow:
    inset 0 0 50px rgba(0, 0, 0, 0.8),
    0 25px 50px -12px rgba(0, 0, 0, 0.7),
    0 0 20px rgba(255, 90, 31, 0.1);
  transition: transform 3s cubic-bezier(0.17, 0.67, 0.12, 0.99);

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__border {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 4px solid rgba(255, 255, 255, 0.1);
    pointer-events: none;
  }

  &__pointer {
    position: absolute;
    top: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 12px solid transparent;
    border-right: 12px solid transparent;
    border-top: 20px solid @gold;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
    z-index: 10;
  }

  &__center {
    position: absolute;
    inset: 0;
    margin: auto;
    width: 56px;
    height: 56px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  }

  &__center-dot {
    width: 28px;
    height: 28px;
    background: @primary;
    border-radius: 50%;
    box-shadow: 0 0 15px @primary;
  }
}

.lr-spin-btn {
  margin-top: 24px;
  width: 100%;
  max-width: 180px;
  background: @primary;
  color: @bg-dark;
  font-weight: 900;
  font-size: var(--vk-fs-md);
  padding: 10px;
  border-radius: 10px;
  border: none;
  text-transform: uppercase;
  letter-spacing: 3px;
  box-shadow: 0 0 20px rgba(255, 90, 31, 0.6), 0 0 40px rgba(255, 90, 31, 0.2);
  cursor: pointer;

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

// ====== REFERRAL ======
.lr-referral {
  padding: 0 16px;
  margin-top: 24px;

  &__label {
    font-size: var(--vk-fs-2xs);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 3px;
    color: var(--vk-t3);
    padding-left: 4px;
    margin-bottom: 8px;
    display: block;
  }

  &__row {
    display: flex;
    gap: 8px;
  }

  &__link {
    flex: 1;
    background: @card-bg;
    border: 1px solid rgba(107, 114, 128, 0.3);
    border-radius: 10px;
    padding: 12px 16px;
    font-size: var(--vk-fs-xs);
    color: #d1d5db;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__copy {
    background: @red;
    color: #ffffff;
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    padding: 12px 20px;
    border-radius: 10px;
    border: none;
    cursor: pointer;

    &:active {
      opacity: 0.8;
    }
  }
}

// ====== RULES ======
.lr-rules {
  margin: 16px 16px 0;
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-radius: 10px;
  border-left: 4px solid @primary;

  &__text {
    font-size: var(--vk-fs-xs);
    line-height: 1.6;
    color: #9ca3af;
  }

  &__highlight {
    color: #ffffff;
    font-weight: 700;
  }
}

// ====== WINNERS ======
.lr-winners {
  padding: 16px;
  margin-top: 8px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 4px;
    margin-bottom: 12px;
  }

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #ffffff;
  }

  &__live {
    font-size: var(--vk-fs-2xs);
    background: rgba(107, 114, 128, 0.3);
    padding: 4px 8px;
    border-radius: 4px;
    color: #9ca3af;
  }

  &__table {
    background: @card-bg;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid rgba(107, 114, 128, 0.2);
  }

  &__thead {
    display: flex;
    justify-content: space-between;
    padding: 8px 16px;
    background: rgba(0, 0, 0, 0.3);
    font-size: var(--vk-fs-2xs);
    text-transform: uppercase;
    color: var(--vk-t3);
    font-weight: 600;
  }

  &__marquee {
    height: 180px;
    overflow: hidden;
    position: relative;
  }

  &__marquee-content {
    display: flex;
    flex-direction: column;
    animation: marquee-vertical 15s linear infinite;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(31, 41, 55, 0.5);
    font-size: var(--vk-fs-xs);
  }

  &__name {
    font-weight: 500;
    color: #e5e7eb;
  }

  &__amount {
    color: @gold;
    font-weight: 700;
  }
}

@keyframes marquee-vertical {
  0% { transform: translateY(0); }
  100% { transform: translateY(-50%); }
}

// ====== POPUP ======
.lr-popup-wrapper {
  background: transparent !important;
  overflow: visible !important;
}

.lr-popup {
  width: calc(100vw - 40px);
  max-width: 360px;
  box-sizing: border-box;
  background: rgba(10, 14, 26, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 90, 31, 0.2);
  box-shadow: 0 0 20px rgba(255, 90, 31, 0.15);
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  &__icon {
    width: 64px;
    height: 64px;
    background: rgba(255, 90, 31, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    font-size: var(--vk-fs-2xl);
    font-weight: 700;
    color: #ffffff;
  }

  &__desc {
    font-size: var(--vk-fs-base);
    color: var(--vk-t2);
    text-align: center;
    padding: 0 16px;
  }

  &__socials {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0 8px;
  }

  &__social {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);

    &--fb { background: #1877f2; }
    &--tg { background: #0088cc; }
    &--wa { background: #25d366; }
    &--x { background: #000000; border: 1px solid rgba(255, 255, 255, 0.2); }
  }

  &__ref {
    width: 100%;

    &-label {
      font-size: var(--vk-fs-2xs);
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 2px;
      color: var(--vk-t3);
      margin-bottom: 8px;
      padding-left: 4px;
    }

    &-row {
      display: flex;
      align-items: center;
      background: #05070a;
      border-radius: 10px;
      padding: 4px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      min-width: 0;
    }

    &-link {
      flex: 1;
      min-width: 0;
      padding: 8px 12px;
      color: #d1d5db;
      font-size: var(--vk-fs-sm);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &-copy {
      flex-shrink: 0;
    }

    &-copy {
      background: @red;
      color: #ffffff;
      font-size: var(--vk-fs-xs);
      font-weight: 700;
      padding: 8px 24px;
      border-radius: 8px;
      border: none;
      cursor: pointer;

      &:active {
        opacity: 0.8;
      }
    }
  }

  &__close {
    width: 100%;
    background: @primary;
    color: #000000;
    font-weight: 900;
    padding: 16px;
    border-radius: 10px;
    font-size: var(--vk-fs-lg);
    border: none;
    cursor: pointer;
    box-shadow: 0 0 20px rgba(255, 90, 31, 0.4);

    &:active {
      transform: scale(0.98);
    }
  }
}

// ====== RESPONSIVE ======
@media (min-width: 769px) {
  .lucky-referral {
    max-width: var(--vk-pc-col);
    margin: 0 auto;
  }
}
</style>
